import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Heading, Paragraph } from '@pandiafi/design-system'
import { ContextNotification } from '@pandiafi/components'

import {
  Contract,
  ContractTenant,
  ModifyContractInput,
  ResidentAppLocale,
  UserMetadata,
} from '../../../../common/validations'
import i18n from '../../../i18n/i18n'
import { useAPIConfirmCreateModification } from '../../../api/apiHooks/useAPIConfirmCreateModification'
import { Section, TextTable } from '../../../components'
import { getTenantName } from '../../../utils/getTenantName'
import { renderDate } from '../../../utils'

type Props = {
  formValues: ModifyContractInput
  tenants: ContractTenant[]
  contract: Contract
  cancel: () => void
  userMetadata: UserMetadata
}

export const ModificationPreview: FC<Props> = ({
  formValues,
  tenants,
  contract,
  cancel,
  userMetadata: { identityProvider, isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'contractModification'])

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIConfirmCreateModification(contract.uuid, lang)

  useEffect(() => {
    if (isSuccess && data && data.link) {
      window.location.href = data.link
    }
  })

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    mutate({
      operationData: formValues,
      provider: identityProvider,
    })
  }

  const terminatedTenants = tenants.filter((tenant) =>
    formValues.removeTenants?.some(
      (removeTenant) => removeTenant.tenantId === tenant.id
    )
  )

  const newContractParty = tenants.find(
    (tenant) => tenant.id === formValues.transfer?.destinationTenantId
  )

  // We want to show the loading spinner until the user is redirected
  const isLoading = isPending || isSuccess

  return (
    <>
      <div className="bg-brand-1 py-8 rounded-t-lg -mt-7 -mx-7 mb-10 text-center">
        <Heading size="l" element="h2">
          {t('contractModification:headings.modificationPreview')}
        </Heading>
      </div>
      <Section className="mb-12">
        <ContextNotification textAlign="center">
          <div className="space-y-2">
            <Paragraph>
              {t('contractModification:texts.confirmationInstruction')}
            </Paragraph>
            <Paragraph>
              <strong>
                {t('contractModification:texts.returnFromAuthReminder')}
              </strong>
            </Paragraph>
          </div>
        </ContextNotification>
      </Section>

      <Section className="mb-12">
        <TextTable className="min-tablet:text-lg">
          {terminatedTenants.map((tenant, index) => (
            <TextTable.Row key={tenant.id}>
              <TextTable.Cell>
                {index === 0
                  ? t('contractModification:labels.movingOutTenant')
                  : ''}
              </TextTable.Cell>
              <TextTable.Cell>{getTenantName(tenant)}</TextTable.Cell>
            </TextTable.Row>
          ))}
          <TextTable.Row>
            <TextTable.Cell>
              {t('contractModification:labels.movingOutDate')}
            </TextTable.Cell>
            <TextTable.Cell>{renderDate(formValues.inEffect)}</TextTable.Cell>
          </TextTable.Row>
          {formValues.description && (
            <TextTable.Row>
              <TextTable.Cell>
                {t('contractModification:labels.modificationDescription')}
              </TextTable.Cell>
              <TextTable.Cell>{formValues.description}</TextTable.Cell>
            </TextTable.Row>
          )}
          {newContractParty && (
            <TextTable.Row>
              <TextTable.Cell>
                {t('contractModification:labels.newContractParty')}
              </TextTable.Cell>
              <TextTable.Cell>{getTenantName(newContractParty)}</TextTable.Cell>
            </TextTable.Row>
          )}
        </TextTable>
      </Section>
      <div className="min-tablet:mt-16 min-tablet:flex min-tablet:items-center min-tablet:justify-end flex gap-4">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('contractModification:buttons.backToEdit')}
        </Button>
        <Button
          type="button"
          variant="delete"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isLoading}
          loading={isLoading}
        >
          {t('contractModification:buttons.confirmModification')}
        </Button>
      </div>
    </>
  )
}
